import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '@/context/CompanyContext';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useNotifications } from '@/hooks/useNotifications';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { useManagementMode } from '@/hooks/useManagementMode';
import { useSecureCart } from '@/hooks/useSecureCart';
import { useToast } from '@/components/ui/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { CaisseLayout } from '@/components/layouts/CaisseLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QrScanner } from '@/components/common/QrScanner';
import { CartTotalBubble } from '@/components/caisse/CartTotalBubble';
import { SearchBar } from '@/components/inventory/SearchBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Plus, Check, Trash2, ShoppingCart, Search, X, Menu, Lock, Minus, Package, RotateCcw, Scan, Camera, Receipt, Clock, User, ChevronDown, ChevronUp, Printer } from 'lucide-react';
import { CardPaymentButton } from '@/components/caisse/CardPaymentButton';
import { SupabaseAddProductModal } from '@/components/inventory/SupabaseAddProductModal';

import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/context/SupabaseInventoryContext';
export const Caisse: React.FC<{
  sidebarTrigger?: React.ReactNode;
}> = ({
  sidebarTrigger
}) => {
  const navigate = useNavigate();
  const {
    currentCompany,
    userRole
  } = useCompany();
  const {
    products,
    visibleProducts,
    hiddenProducts,
    refreshProducts
  } = useSupabaseInventory();
  const {
    hasAccess
  } = useSubscription();
  const {
    success,
    error
  } = useNotifications();
  
  // Secure cart hook with backend calculations
  const {
    cart,
    cartTotal,
    isCalculating,
    addToCart: secureAddToCart,
    updateQuantity: secureUpdateQuantity,
    removeFromCart: secureRemoveFromCart,
    clearCart: secureClearCart,
    processSale: secureProcessSale
  } = useSecureCart(currentCompany?.id);
  const {
    showPrices,
    setShowPrices,
    isAdmin
  } = useCompanySettings();
  const { isRetraitMode, loading: modeLoading } = useManagementMode();
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilter, setSearchFilter] = useState<'all' | 'name' | 'barcode'>('all');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dailySearchCount, setDailySearchCount] = useState(0);
  const [showAllProducts, setShowAllProducts] = useState(true);
  const [gridColumns, setGridColumns] = useState<2 | 3>(2);
  const [productFilter, setProductFilter] = useState<'all' | 'best_seller' | 'en_promotion' | 'low_stock'>('all');
  const [productSort, setProductSort] = useState<'name' | 'price_asc' | 'price_desc' | 'stock_asc' | 'stock_desc'>('name');
  const [selectedProductIndex, setSelectedProductIndex] = useState<number>(0);
  const canAccess = userRole === 'admin' || userRole === 'member' || userRole === 'vendeur';
  const searchInputRef = useRef<HTMLInputElement>(null);
  const productGridRef = useRef<HTMLDivElement>(null);
  
  // États pour la détection des douchettes Bluetooth
  const [lastInputTime, setLastInputTime] = useState(0);
  const [inputBuffer, setInputBuffer] = useState('');
  
  // États pour le modal d'ajout de produit
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [prefilledProductData, setPrefilledProductData] = useState<any>(null);

  // Vérifier le mode de gestion et rediriger si nécessaire
  useEffect(() => {
    if (!modeLoading && isRetraitMode) {
      navigate('/retrait', { replace: true });
    }
  }, [isRetraitMode, modeLoading, navigate]);

  // Focus sur le champ de recherche au chargement
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Load daily search count from localStorage
  useEffect(() => {
    const today = new Date().toDateString();
    const storedData = localStorage.getItem('caisseSearchData');
    if (storedData) {
      const {
        date,
        count
      } = JSON.parse(storedData);
      if (date === today) {
        setDailySearchCount(count);
      } else {
        // Reset count for new day
        localStorage.setItem('caisseSearchData', JSON.stringify({
          date: today,
          count: 0
        }));
        setDailySearchCount(0);
      }
    } else {
      localStorage.setItem('caisseSearchData', JSON.stringify({
        date: today,
        count: 0
      }));
    }
  }, []);

  // Vérification des permissions
  if (!canAccess) {
    return <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="p-6 sm:p-8 text-center max-w-md w-full">
          <Package className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-semibold mb-2">Accès non autorisé</h2>
          <p className="text-muted-foreground text-sm sm:text-base">Vous n'avez pas les permissions pour accéder à la caisse.</p>
        </Card>
      </div>;
  }
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };
  const handleBarcodeScanned = async (barcode: string) => {
    // Check search limit only for demo users who are company owners (not invited members)
    if (!hasAccess && userRole === 'admin') {
      if (dailySearchCount >= 15) {
        toast({
          title: "Limite de recherche atteinte",
          description: "Vous avez atteint la limite de 15 recherches par jour. Passez à Premium pour un accès illimité.",
          variant: "destructive"
        });
        return;
      }

      // Increment search count
      const newCount = dailySearchCount + 1;
      setDailySearchCount(newCount);

      // Save to localStorage
      const today = new Date().toDateString();
      localStorage.setItem('caisseSearchData', JSON.stringify({
        date: today,
        count: newCount
      }));
    }
    
    const normalizedBarcode = barcode.trim();
    const product = products.find(p => p.barcode === normalizedBarcode);
    
    if (product) {
      // Ajout automatique au panier lors du scan ou saisie manuelle
      addProductDirectly(product);
    } else {
      // Rechercher dans OpenFoodFacts
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          error({
            title: "Erreur d'authentification",
            description: "Veuillez vous reconnecter pour continuer."
          });
          return;
        }

        console.log('[CAISSE] Calling barcode-lookup for:', normalizedBarcode);

        const { data, error: lookupError } = await supabase.functions.invoke('barcode-lookup', {
          body: { barcode: normalizedBarcode }
        });

        console.log('[CAISSE] Barcode lookup response:', { data, error: lookupError });

        if (lookupError) {
          console.error('[CAISSE] Barcode lookup error:', lookupError);
          error({
            title: "Produit non trouvé",
            description: `Aucun produit trouvé avec le code-barres: ${normalizedBarcode}`
          });
          return;
        }

        // Vérifier que data existe et est un objet valide
        if (!data || typeof data !== 'object') {
          console.error('[CAISSE] Invalid response data:', data);
          error({
            title: "Produit non trouvé",
            description: `Aucun produit trouvé avec le code-barres: ${normalizedBarcode}`
          });
          return;
        }

        if (data.success === true && data.found === true && data.product && typeof data.product === 'object') {
          const productName = data.product.name || 'Produit inconnu';
          
          // Produit trouvé dans OpenFoodFacts, ouvrir le modal d'ajout avec données pré-remplies
          setPrefilledProductData({
            name: productName,
            barcode: normalizedBarcode,
            price: 0,
            quantity: 0,
            minThreshold: 5
          });
          setShowAddProductModal(true);
          
          toast({
            title: "Produit trouvé",
            description: `Produit "${productName}" trouvé dans OpenFoodFacts. Complétez les informations.`,
          });
        } else {
          // Produit non trouvé même dans OpenFoodFacts
          error({
            title: "Produit non trouvé",
            description: `Aucun produit trouvé avec le code-barres: ${normalizedBarcode}`
          });
        }
      } catch (err) {
        console.error('Erreur lors de la recherche OpenFoodFacts:', err);
        error({
          title: "Produit non trouvé",
          description: `Aucun produit trouvé avec le code-barres: ${normalizedBarcode}`
        });
      }
    }
  };

  // Recherche en temps réel
  const [filteredVisibleProducts, setFilteredVisibleProducts] = useState<Product[]>([]);
  const [filteredHiddenProducts, setFilteredHiddenProducts] = useState<Product[]>([]);
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredVisibleProducts([]);
      setFilteredHiddenProducts([]);
      return;
    }
    const filterProducts = (productList: Product[]) => {
      const query = searchQuery.toLowerCase();
      return productList.filter(product => {
        switch (searchFilter) {
          case 'name':
            return product.name.toLowerCase().includes(query);
          case 'barcode':
            return product.barcode?.toLowerCase().includes(query);
          default:
            return product.name.toLowerCase().includes(query) || product.barcode?.toLowerCase().includes(query);
        }
      });
    };
    setFilteredVisibleProducts(filterProducts(visibleProducts).slice(0, 10));
    setFilteredHiddenProducts(filterProducts(hiddenProducts).slice(0, 5));
  }, [searchQuery, visibleProducts, hiddenProducts, searchFilter]);

  // Combine visible and hidden for display
  const filteredProducts = [...filteredVisibleProducts, ...filteredHiddenProducts];

  // Filtrer et trier les produits populaires
  const filteredAndSortedProducts = React.useMemo(() => {
    let filtered = [...visibleProducts];
    
    // Appliquer les filtres
    switch (productFilter) {
      case 'best_seller':
        filtered = filtered.filter(p => p.cycle_de_vie === 'best_seller');
        break;
      case 'en_promotion':
        filtered = filtered.filter(p => p.cycle_de_vie === 'en_promotion');
        break;
      case 'low_stock':
        filtered = filtered.filter(p => p.quantity <= p.min_threshold);
        break;
      default:
        // 'all' - pas de filtre
        break;
    }
    
    // Appliquer le tri
    switch (productSort) {
      case 'price_asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'stock_asc':
        filtered.sort((a, b) => a.quantity - b.quantity);
        break;
      case 'stock_desc':
        filtered.sort((a, b) => b.quantity - a.quantity);
        break;
      case 'name':
      default:
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }
    
    return filtered;
  }, [visibleProducts, productFilter, productSort]);

  // Navigation au clavier dans la grille de produits (désactivé sur mobile)
  useEffect(() => {
    if (isMobile) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ne pas intercepter si on est dans un input
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      const gridCols = window.innerWidth >= 640 ? 4 : 2;
      const maxIndex = filteredAndSortedProducts.length - 1;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          setSelectedProductIndex(prev => Math.min(prev + 1, maxIndex));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setSelectedProductIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedProductIndex(prev => Math.min(prev + gridCols, maxIndex));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedProductIndex(prev => Math.max(prev - gridCols, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredAndSortedProducts[selectedProductIndex]) {
            addProductDirectly(filteredAndSortedProducts[selectedProductIndex]);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredAndSortedProducts, selectedProductIndex, isMobile]);

  // Auto-scroll pour suivre le produit sélectionné (désactivé sur mobile)
  useEffect(() => {
    if (isMobile) return;
    
    if (productGridRef.current) {
      const productCards = productGridRef.current.children;
      const selectedCard = productCards[selectedProductIndex] as HTMLElement;
      
      if (selectedCard) {
        selectedCard.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
      }
    }
  }, [selectedProductIndex, isMobile]);

  const addProductDirectly = async (product: Product) => {
    if (product.quantity === 0) {
      error({
        title: "Stock épuisé",
        description: `Le produit ${product.name} n'est plus en stock.`
      });
      return;
    }

    // Use secure backend add to cart
    const success = await secureAddToCart(product, 1);

    if (success) {
      // Vider le champ de recherche seulement si c'est un code-barres
      if (/^\d{8,13}$/.test(searchQuery.trim())) {
        setSearchQuery('');
      }
    }
  };
  const removeFromCart = async (productId: string) => {
    await secureRemoveFromCart(productId);
  };
  
  const updateCartQuantity = async (productId: string, newQuantity: number) => {
    await secureUpdateQuantity(productId, newQuantity);
  };
  
  const clearCart = () => {
    secureClearCart();
  };
  
  const getTotalPrice = () => {
    // Use server-calculated total from secure cart
    return cartTotal.total;
  };
  const validateSale = () => {
    if (cart.length === 0) {
      error({
        title: "Panier vide",
        description: "Ajoutez des produits avant de valider la vente."
      });
      return;
    }
    setIsConfirmModalOpen(true);
  };
  const confirmSale = async (paymentMethod: 'Espèces' | 'Carte Bancaire' | 'Chèque' | 'Autre' = 'Espèces') => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    try {
      // Use secure backend sale processing
      const saleSuccess = await secureProcessSale(paymentMethod);
      
      if (saleSuccess) {
        await refreshProducts();
        setIsConfirmModalOpen(false);
      }
    } catch (err) {
      error({
        title: "Erreur lors de la vente",
        description: err instanceof Error ? err.message : "Impossible de traiter la vente. Veuillez réessayer."
      });
    } finally {
      setIsProcessing(false);
    }
  };
  const handleCardPaymentSuccess = () => {
    confirmSale('Carte Bancaire');
  };

  const handlePrintReceipt = () => {
    const printContent = `
      ╔══════════════════════════════════╗
      ║           TICKET DE CAISSE       ║
      ╚══════════════════════════════════╝
      
      ${currentCompany?.name || 'Ma Boutique'}
      ${new Date().toLocaleString('fr-FR')}
      ────────────────────────────────────
      
      ${cart.map(item => 
        `${item.product.name}\n   ${item.quantity}x ${formatPrice(item.product.price)} = ${formatPrice(item.product.price * item.quantity)}`
      ).join('\n\n')}
      
      ────────────────────────────────────
      TOTAL: ${formatPrice(getTotalPrice())}
      ────────────────────────────────────
      
      Merci de votre visite !
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Ticket de Caisse</title>
            <style>
              body {
                font-family: 'Courier New', monospace;
                margin: 0;
                padding: 20px;
                white-space: pre-line;
                font-size: 12px;
                line-height: 1.4;
              }
              @media print {
                body { margin: 0; padding: 10px; }
              }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      printWindow.close();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Gestionnaire pour détecter les douchettes Bluetooth
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const currentTime = Date.now();
    
    setSearchQuery(value);
    
    // Détecter les saisies rapides typiques des douchettes (plus de 5 caractères tapés en moins de 200ms)
    if (value.length > inputBuffer.length && currentTime - lastInputTime < 200) {
      setInputBuffer(value);
    } else {
      setInputBuffer(value);
    }
    
    setLastInputTime(currentTime);
  };

  // Gestionnaire pour détecter la validation automatique des douchettes
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Si Enter est pressé et que la saisie semble provenir d'une douchette
    if (e.key === 'Enter') {
      const value = searchQuery.trim();
      
      // Vérifier si c'est un code-barres (chiffres uniquement, 8-13 caractères)
      if (/^\d{8,13}$/.test(value)) {
        handleBarcodeScanned(value);
      }
    }
  };
  const caisseActions = <div className="flex items-center gap-2 sm:gap-3">
      {isAdmin}
      <Badge variant="outline" className="px-2 sm:px-3 py-1 text-xs sm:text-sm">
        {cart.length} article{cart.length > 1 ? 's' : ''}
      </Badge>
      {cart.length > 0 && showPrices && <Badge variant="default" className="px-2 sm:px-3 py-1 text-xs sm:text-sm">
          {formatPrice(getTotalPrice())}
        </Badge>}
    </div>;

  // Composant Panier réutilisable
  const CartComponent = ({
    className = ""
  }: {
    className?: string;
  }) => {
    return (
      <Card className={`sticky top-4 z-10 ${className}`}>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
            Panier {caisseActions}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {cart.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Panier vide</p>
            ) : (
              <>
                <div className="space-y-2 max-h-64 sm:max-h-80 lg:max-h-96 xl:max-h-[500px] overflow-y-auto pb-32 sm:pb-0">
                  {cart.map(item => (
                    <div key={item.product.id} className="flex items-center justify-between p-2 border rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.product.name}</p>
                        <div className="text-xs text-muted-foreground">
                          {showPrices && <span>{formatPrice(item.product.price)}</span>}
                          <span className="ml-2">Qté: {item.quantity}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                          className="h-6 w-6 p-0"
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="mx-2 text-sm font-medium w-8 text-center">{item.quantity}</span>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                          className="h-6 w-6 p-0"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={() => removeFromCart(item.product.id)}
                          className="h-6 w-6 p-0 ml-1"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Section fixe en bas - Mobile uniquement */}
                <div className="fixed bottom-0 left-0 right-0 bg-card border-t p-4 shadow-lg z-20 xl:hidden">
                  {showPrices && (
                    <div className="mb-4">
                      <div className="flex justify-between items-center font-bold text-lg">
                        <span>Total:</span>
                        <span>{formatPrice(getTotalPrice())}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1" 
                      onClick={clearCart}
                    >
                      Vider
                    </Button>
                    <Button 
                      className="flex-1" 
                      onClick={() => setIsConfirmModalOpen(true)}
                      disabled={cart.length === 0}
                    >
                      Valider
                    </Button>
                  </div>
                </div>
                
                {/* Section boutons - Desktop uniquement */}
                <div className="hidden xl:block pt-4">
                  {showPrices && (
                    <div className="border-t pt-4 mb-4">
                      <div className="flex justify-between items-center font-bold text-lg">
                        <span>Total:</span>
                        <span>{formatPrice(getTotalPrice())}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1" 
                      onClick={clearCart}
                    >
                      Vider
                    </Button>
                    <Button 
                      className="flex-1" 
                      onClick={() => setIsConfirmModalOpen(true)}
                      disabled={cart.length === 0}
                    >
                      Valider
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };
  return <div className="caisse-theme">
    <CaisseLayout title="Point de Vente" actions={caisseActions} sidebarTrigger={sidebarTrigger}>
      <div className="container mx-auto max-w-full px-4 sm:px-6">
        {/* Layout Mobile-First avec stacking vertical */}
        <div className="flex flex-col xl:flex-row gap-4 sm:gap-6">
          {/* Section Scanner/Recherche - Mobile: pleine largeur, Desktop: 2/3 largeur */}
          <div className="flex-1 xl:flex-[2] space-y-4 sm:space-y-6">
            
            {/* Scanner Section */}
            <Card className="hover-lift border-l-4 border-l-green-500">
              <CardHeader className="pb-6">
                <CardTitle className="flex items-center gap-2 text-lg text-green-700 dark:text-green-400">
                  <Scan className="w-5 h-5" />
                  Scanner / Rechercher un produit
                  <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">VENTE</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Section de recherche */}
                <div className="space-y-3 sm:space-y-4">
                  <div className="relative">
                    <Input 
                      ref={searchInputRef}
                      placeholder="Rechercher un produit ou saisir code-barres..." 
                      value={searchQuery} 
                      onChange={handleSearchInputChange}
                      onKeyDown={handleSearchKeyDown}
                      disabled={!hasAccess && userRole === 'admin' && dailySearchCount >= 15} 
                      className={`w-full h-10 sm:h-11 ${searchQuery ? "pr-16 sm:pr-20" : ""}`} 
                    />
                    {searchQuery && <Button variant="outline" size="sm" onClick={handleClearSearch} className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 h-7 sm:h-8 text-xs px-2 sm:px-3">
                        <span className="hidden sm:inline">Effacer</span>
                        <span className="sm:hidden">✕</span>
                      </Button>}
                  </div>
                  {!hasAccess && userRole === 'admin' && <div className="text-xs text-muted-foreground px-1">
                      Mode démo: Recherches utilisées: {dailySearchCount}/15 par jour
                    </div>}
                </div>

                {/* Section scanner QR */}
                <div className="border-t pt-4 sm:pt-6">
                  <QrScanner onResult={handleBarcodeScanned} className="w-full" />
                </div>

                {/* Résultats de recherche */}
                {filteredProducts.length > 0 && <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}:
                    </p>
                    <div className="grid gap-2">
                      {filteredProducts.map(product => {
                    const isHidden = hiddenProducts.includes(product);
                    return <div key={product.id} className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${isHidden ? 'blur-sm opacity-50 pointer-events-none cursor-not-allowed bg-muted/50' : 'hover:bg-accent cursor-pointer'}`} onClick={() => !isHidden && addProductDirectly(product)}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm sm:text-base truncate">{product.name}</span>
                                {isHidden && <Badge variant="outline" className="text-xs">
                                    Premium requis
                                  </Badge>}
                                {!isHidden && product.quantity <= product.min_threshold && <Badge variant="destructive" className="text-xs">
                                    Stock faible
                                  </Badge>}
                              </div>
                              <div className="text-xs sm:text-sm text-muted-foreground space-x-2">
                                {showPrices && <span>{formatPrice(product.price)}</span>}
                                <span>Stock: {product.quantity}</span>
                                {product.barcode && <span>Code: {product.barcode}</span>}
                              </div>
                            </div>
                            <Button size="sm" className="ml-2 h-8 px-3 text-xs" disabled={isHidden} onClick={e => {
                        e.stopPropagation();
                        if (!isHidden) addProductDirectly(product);
                      }}>
                              {isHidden ? 'Bloqué' : 'Ajouter'}
                            </Button>
                          </div>;
                  })}
                    </div>
                    {filteredHiddenProducts.length > 0 && <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800 mb-2">
                          {filteredHiddenProducts.length} produit{filteredHiddenProducts.length > 1 ? 's' : ''} supplémentaire{filteredHiddenProducts.length > 1 ? 's' : ''} disponible{filteredHiddenProducts.length > 1 ? 's' : ''} avec Premium
                        </p>
                        <p className="text-xs text-amber-600">
                          Passez à Premium pour débloquer tous vos produits
                        </p>
                      </div>}
                  </div>}
              </CardContent>
            </Card>

            {/* Panier - Version mobile uniquement */}
            <div className="xl:hidden">
              <CartComponent />
            </div>

            {/* Produits populaires */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                      <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                      Tous les produits ({filteredAndSortedProducts.length})
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setShowAllProducts(!showAllProducts)} className="flex items-center gap-1 h-8 px-2">
                        {showAllProducts ? <>
                            <ChevronUp className="w-4 h-4" />
                            <span className="text-xs">Moins</span>
                          </> : <>
                            <ChevronDown className="w-4 h-4" />
                            <span className="text-xs">Plus</span>
                          </>}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Filtres et tri */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={productFilter} onValueChange={(value: any) => setProductFilter(value)}>
                      <SelectTrigger className="w-full sm:w-[180px] h-9 text-xs">
                        <SelectValue placeholder="Filtrer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les produits</SelectItem>
                        <SelectItem value="best_seller">Best-sellers</SelectItem>
                        <SelectItem value="en_promotion">En promotion</SelectItem>
                        <SelectItem value="low_stock">Stock bas</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select value={productSort} onValueChange={(value: any) => setProductSort(value)}>
                      <SelectTrigger className="w-full sm:w-[180px] h-9 text-xs">
                        <SelectValue placeholder="Trier par" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">Nom (A-Z)</SelectItem>
                        <SelectItem value="price_asc">Prix croissant</SelectItem>
                        <SelectItem value="price_desc">Prix décroissant</SelectItem>
                        <SelectItem value="stock_asc">Stock croissant</SelectItem>
                        <SelectItem value="stock_desc">Stock décroissant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div ref={productGridRef} className="grid gap-3 grid-cols-2 sm:grid-cols-4">
                  {(showAllProducts ? filteredAndSortedProducts : filteredAndSortedProducts.slice(0, gridColumns === 2 ? 8 : 12)).map((product, index) => {
                    const isInCart = cart.some(item => item.product.id === product.id);
                    const isSelected = !isMobile && index === selectedProductIndex;
                    return <div 
                      key={product.id} 
                      className={`flex flex-col p-3 border rounded-lg hover:bg-accent cursor-pointer transition-all ${isInCart ? 'border-green-500 border-2' : ''} ${isSelected ? 'ring-2 ring-primary ring-offset-2 bg-accent' : ''}`}
                      onClick={() => {
                        setSelectedProductIndex(index);
                        addProductDirectly(product);
                      }}
                    >
                      {product.image_url && (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-24 object-cover rounded-md mb-2"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1 flex flex-col">
                        <p className="font-medium text-sm truncate mb-1">{product.name}</p>
                        <div className="text-xs text-muted-foreground mb-2 space-y-1">
                          {showPrices && <div>{formatPrice(product.price)}</div>}
                          <div>Stock: {product.quantity}</div>
                        </div>
                        <Button size="sm" className="w-full h-7 text-xs mt-auto" onClick={e => {
                          e.stopPropagation();
                          addProductDirectly(product);
                        }}>
                          Ajouter
                        </Button>
                      </div>
                    </div>;
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Section Panier - Mobile: pleine largeur, Desktop: 1/3 largeur */}
          <div className="hidden xl:block xl:flex-1">
            <CartComponent />
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={isConfirmModalOpen} onOpenChange={setIsConfirmModalOpen}>
        <DialogContent className="max-w-lg w-[95vw] mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Receipt className="w-5 h-5" />
              Confirmer la vente
            </DialogTitle>
            <DialogDescription className="text-sm">
              Récapitulatif de votre vente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 w-full overflow-hidden">
            {/* Articles */}
            <div className="space-y-2">
              {cart.map(item => <div key={item.product.id} className="flex justify-between items-center text-sm min-w-0 gap-2">
                  <span className="truncate flex-1 min-w-0 text-left max-w-[60%]">{item.product.name}</span>
                  <div className="flex items-center gap-2 text-muted-foreground shrink-0">
                    <span className="whitespace-nowrap">{item.quantity}x</span>
                    {showPrices && <span className="whitespace-nowrap">{formatPrice(item.product.price)}</span>}
                  </div>
                </div>)}
            </div>

            {/* Total */}
            {showPrices && <div className="border-t pt-3 space-y-3">
                <div className="flex justify-between items-center font-semibold">
                  <span>Total à encaisser:</span>
                  <span className="text-lg">{formatPrice(getTotalPrice())}</span>
                </div>
                
                {/* Bouton Imprimer */}
                <div className="flex justify-center">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handlePrintReceipt}
                    className="flex items-center gap-2 text-xs"
                  >
                    <Printer className="w-4 h-4" />
                    <span className="hidden xs:inline">Imprimer le ticket</span>
                    <span className="xs:hidden">Imprimer</span>
                  </Button>
                </div>
              </div>}
          </div>

          <DialogFooter className="flex-col space-y-2 sm:space-y-0 sm:flex-row">
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              {/* Boutons de paiement */}
              <div className="flex flex-col gap-2 flex-1">
                <Button onClick={() => confirmSale('Espèces')} disabled={isProcessing} className="w-full h-10 text-sm">
                  {isProcessing ? <LoadingSpinner variant="minimal" size="sm" /> : 'Espèces'}
                </Button>
                <div className="flex flex-col gap-2">
                   <div className="flex gap-2">
                     <CardPaymentButton amount={getTotalPrice()} onPaymentSuccess={handleCardPaymentSuccess} disabled={isProcessing} />
                     <Button onClick={() => confirmSale('Chèque')} variant="outline" disabled={isProcessing} className="flex-1 h-9 text-xs">
                       Chèque
                     </Button>
                   </div>
                 </div>
              </div>
            </div>
            
            {/* Bouton d'annulation */}
            <div className="flex justify-center w-full sm:w-auto">
            <Button variant="ghost" onClick={() => setIsConfirmModalOpen(false)} disabled={isProcessing} className="h-9 px-4 text-sm">
              Annuler
            </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <SupabaseAddProductModal
        isOpen={showAddProductModal}
        onClose={() => {
          setShowAddProductModal(false);
          setPrefilledProductData(null);
        }}
        editingProduct={prefilledProductData}
      />
    </CaisseLayout>
  </div>;
};